import React, { useState, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import esLocale from "@fullcalendar/core/locales/es";
import { Badge, Modal } from "react-bootstrap";

import { notifyError } from "jsx/components/CitoPlus/components/CitoPlusToastr";
import VigilanteZonasSocialesService from "../../../../../services/VigilanteZonasSocialesService";
import ReporteAveriaModal from "./ReporteAveriaModal";

const CalendarioReservasVigilante = () => {
  const calendarRef = useRef(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showReporteModal, setShowReporteModal] = useState(false);
  const [reportes, setReportes] = useState([]);
  const [loadingReportes, setLoadingReportes] = useState(false);

  const fetchEvents = async (fetchInfo, successCallback, failureCallback) => {
    try {
      const response = await VigilanteZonasSocialesService.getCalendario(
        fetchInfo.startStr,
        fetchInfo.endStr,
      );
      successCallback(response.data);
    } catch (error) {
      console.error("Error fetching events:", error);
      notifyError("Error al cargar el calendario de reservas");
      failureCallback(error);
    }
  };

  const handleEventClick = async (clickInfo) => {
    const eventData = {
      ...clickInfo.event.extendedProps,
      title: clickInfo.event.title,
      start: clickInfo.event.start,
      end: clickInfo.event.end,
    };
    setSelectedEvent(eventData);
    setShowModal(true);

    // Fetch reportes para esta reserva
    if (eventData.uuid) {
      await fetchReportes(eventData.uuid);
    }
  };

  const fetchReportes = async (reservaUuid) => {
    setLoadingReportes(true);
    try {
      const response =
        await VigilanteZonasSocialesService.getReportesAverias(reservaUuid);
      setReportes(response.data.results || response.data);
    } catch (error) {
      console.error("Error fetching reportes:", error);
      setReportes([]);
    } finally {
      setLoadingReportes(false);
    }
  };

  const getEstadoBadge = (estado) => {
    const estados = {
      confirmada: <Badge bg="success">Confirmada</Badge>,
      pendiente: <Badge bg="warning">Pendiente</Badge>,
      cancelada: <Badge bg="danger">Cancelada</Badge>,
      incumplida: <Badge bg="secondary">Incumplida</Badge>,
    };
    return estados[estado] || <Badge bg="secondary">{estado}</Badge>;
  };

  const formatDateTime = (value) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";
    return new Intl.DateTimeFormat("es-CO", {
      dateStyle: "full",
      timeStyle: "short",
    }).format(date);
  };

  return (
    <>
      <div className="card">
        <div className="card-header">
          <h4 className="card-title mb-0">Calendario de Reservas</h4>
        </div>
        <div className="card-body">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay",
            }}
            locale={esLocale}
            events={fetchEvents}
            eventClick={handleEventClick}
            height="auto"
            eventDisplay="block"
            eventTimeFormat={{
              hour: "2-digit",
              minute: "2-digit",
              meridiem: false,
            }}
          />
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Detalle de Reserva</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedEvent && (
            <div className="vstack gap-3">
              {/* Header con título y estado */}
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 p-3 bg-primary text-white rounded">
                <div>
                  <h5 className="mb-1 fw-bold">
                    {selectedEvent.title || "Sin título"}
                  </h5>
                  <div>
                    <i className="fas fa-map-marker-alt me-1"></i>
                    {selectedEvent.zona_nombre || "No disponible"}
                  </div>
                </div>
                {getEstadoBadge(selectedEvent.estado)}
              </div>

              {/* Horario - Sección destacada */}
              <section className="border rounded p-3">
                <h6 className="text-uppercase text-muted mb-3 fw-semibold">
                  <i className="fas fa-clock me-2"></i>Horario
                </h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label text-muted small mb-1">
                      Inicio
                    </label>
                    <div className="fw-medium">
                      {formatDateTime(selectedEvent.start)}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-muted small mb-1">
                      Fin
                    </label>
                    <div className="fw-medium">
                      {formatDateTime(selectedEvent.end)}
                    </div>
                  </div>
                </div>
              </section>

              {/* Residente e Inmueble */}
              <section className="border rounded p-3">
                <h6 className="text-uppercase text-muted mb-3 fw-semibold">
                  <i className="fas fa-user me-2"></i>Residente e Inmueble
                </h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label text-muted small mb-1">
                      Residente
                    </label>
                    <div className="fw-medium">
                      {selectedEvent.residente_nombre || "No disponible"}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label text-muted small mb-1">
                      Inmueble
                    </label>
                    <div className="fw-medium">
                      {selectedEvent.inmueble || "No disponible"}
                    </div>
                  </div>
                </div>
              </section>

              {/* Información de la Zona */}
              <section className="border rounded p-3">
                <h6 className="text-uppercase text-muted mb-3 fw-semibold">
                  <i className="fas fa-building me-2"></i>Información de la Zona
                </h6>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label text-muted small mb-1">
                      Responsable
                    </label>
                    <div className="fw-medium">
                      {selectedEvent.zona_responsable || "No disponible"}
                    </div>
                  </div>
                  {selectedEvent.descripcion && (
                    <div className="col-12">
                      <label className="form-label text-muted small mb-1">
                        Descripción
                      </label>
                      <div>{selectedEvent.descripcion}</div>
                    </div>
                  )}
                  <div className="col-12">
                    <label className="form-label text-muted small mb-1">
                      Reglas de Uso
                    </label>
                    <div
                      className="small"
                      dangerouslySetInnerHTML={{
                        __html:
                          selectedEvent.zona_reglas_uso ||
                          "<em class='text-muted'>Sin reglas definidas</em>",
                      }}
                    />
                  </div>
                </div>
              </section>

              {/* Reportes de Averías/Faltantes */}
              <section className="border rounded p-3">
                <h6 className="text-uppercase text-muted mb-3 fw-semibold">
                  <i className="fas fa-exclamation-triangle me-2"></i>Reportes
                  de Averías/Faltantes
                </h6>
                {loadingReportes ? (
                  <div className="text-center py-3">
                    <div
                      className="spinner-border spinner-border-sm"
                      role="status"
                    >
                      <span className="visually-hidden">Cargando...</span>
                    </div>
                    <span className="ms-2 text-muted">
                      Cargando reportes...
                    </span>
                  </div>
                ) : reportes.length > 0 ? (
                  <div className="list-group">
                    {reportes.map((reporte) => (
                      <div
                        key={reporte.uuid}
                        className="list-group-item list-group-item-action flex-column align-items-start active"
                      >
                        <div className="d-flex w-100 justify-content-between">
                          <h6 className="mb-2 text-white">
                            <Badge bg="danger">{reporte.tipo_display}</Badge>
                          </h6>
                          <small className="text-white">
                            {reporte.created_at}
                          </small>
                        </div>
                        {reporte.detalle && (
                          <p className="mb-1">{reporte.detalle}</p>
                        )}
                        <small className="text-white">
                          <i className="fas fa-user-edit me-1"></i>
                          {reporte.creado_por_nombre}
                        </small>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted text-center py-3">
                    <i className="fas fa-check-circle me-2"></i>
                    No hay reportes para esta reserva
                  </div>
                )}
              </section>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-warning me-2"
            onClick={() => setShowReporteModal(true)}
          >
            <i className="fas fa-exclamation-triangle me-1"></i>
            Reportar Avería/Faltante
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowModal(false)}
          >
            Cerrar
          </button>
        </Modal.Footer>
      </Modal>

      <ReporteAveriaModal
        reservaUuid={selectedEvent?.uuid}
        show={showReporteModal}
        onHide={() => setShowReporteModal(false)}
        onSuccess={() =>
          selectedEvent?.uuid && fetchReportes(selectedEvent.uuid)
        }
      />
    </>
  );
};

export default CalendarioReservasVigilante;
